"use client";

import { ReactNode, useEffect, useRef, useState } from "react";
import { Wheel, WheelOption } from "@/data/wheels";
import { SPIN_DURATION_MS } from "@/lib/constants";
import WheelHeat from "@/components/WheelHeat";

// Vivid, cohesive jewel-tone palette that pops on the dark background.
const PALETTE = [
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#3b82f6", // blue
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // red
  "#06b6d4", // cyan
  "#a855f7", // purple
];

// Crisp black outline so labels stay readable on any segment color.
const LABEL_OUTLINE =
  "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 2px 3px rgba(0,0,0,0.7)";

const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4);

interface ButtonConfig {
  label: string;
  onClick: () => void;
  disabled: boolean;
}

interface SpinningWheelProps {
  wheel: Wheel;
  spinToken: number;
  resultOption: WheelOption | null;
  onSpinEnd: () => void;
  onTick: (strength: number) => void;
  showResult: boolean;
  showValue: boolean;
  // When omitted, the hub is a static decoration (used for the auto-spinning
  // chaos wheel that the player doesn't click to start).
  spinButton?: ButtonConfig;
  // Rendered in the slot beneath the wheel (advance button, countdown, etc.).
  footer?: ReactNode;
  // Overrides the disc size (e.g. a compact spectator wheel). Defaults to the
  // full-size game wheel.
  discClassName?: string;
  // Positive-roll "hot streak" tier (0 = none, 1–3 = warm → inferno). Drives the
  // on-fire overlay behind the disc.
  heat?: number;
}

export default function SpinningWheel({
  wheel,
  spinToken,
  resultOption,
  onSpinEnd,
  onTick,
  showResult,
  showValue,
  spinButton,
  footer,
  // On phones the disc is capped by the *dynamic* viewport height (dvh) so the
  // whole wheel + spin hub always fits between the slim top banner and the
  // bottom music bar without scrolling. Desktop (md+) keeps the original size.
  discClassName = "w-[min(86vw,40dvh,26rem)] md:w-[min(85vw,62vh,38rem)]",
  heat = 0,
}: SpinningWheelProps) {
  const wheelRef = useRef<HTMLDivElement>(null);
  const pointerRef = useRef<HTMLDivElement>(null);
  const labelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const rotationRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const [restRotation, setRestRotation] = useState(0);

  const segmentCount = wheel.options.length;
  const segmentAngle = 360 / segmentCount;

  const resultIndex = resultOption ? wheel.options.indexOf(resultOption) : -1;
  const resultColor =
    resultIndex >= 0 ? PALETTE[resultIndex % PALETTE.length] : PALETTE[0];

  // Counter-rotate every label so the text stays UPRIGHT no matter how far the
  // wheel has turned. The labels orbit with the wheel but never tilt or flip.
  const applyLabelTransforms = (rot: number) => {
    for (let i = 0; i < segmentCount; i++) {
      const el = labelRefs.current[i];
      if (!el) continue;
      const centerDeg = i * segmentAngle + segmentAngle / 2;
      el.style.transform = `rotate(${-(rot + centerDeg)}deg)`;
    }
  };

  useEffect(() => {
    if (spinToken === 0 || !resultOption) return;

    const index = wheel.options.indexOf(resultOption);
    const targetCenter = index * segmentAngle + segmentAngle / 2;
    const extraSpins = 6;
    const start = rotationRef.current;
    const current = start % 360;
    // Land the winning segment under the fixed top pointer.
    const delta = ((360 - targetCenter - current) % 360 + 360) % 360;
    const end = start + extraSpins * 360 + delta;

    const startTime = performance.now();
    let lastPeg = Math.floor(start / segmentAngle);
    let pointerKick = 0;

    const frame = (now: number) => {
      const t = Math.min((now - startTime) / SPIN_DURATION_MS, 1);
      const eased = easeOutQuart(t);
      const cur = start + (end - start) * eased;

      if (wheelRef.current) {
        wheelRef.current.style.transform = `rotate(${cur}deg)`;
      }
      applyLabelTransforms(cur);

      const peg = Math.floor(cur / segmentAngle);
      if (peg !== lastPeg) {
        lastPeg = peg;
        onTick(eased);
        pointerKick = -26; // a segment edge just struck the pointer
      }

      // Spring the pointer back toward rest each frame for a snappy flick.
      pointerKick += (0 - pointerKick) * 0.35;
      if (pointerRef.current) {
        pointerRef.current.style.transform = `translateX(-50%) rotate(${pointerKick}deg)`;
      }

      if (t < 1) {
        rafRef.current = requestAnimationFrame(frame);
      } else {
        rotationRef.current = end;
        setRestRotation(end);
        if (pointerRef.current) {
          pointerRef.current.style.transform = "translateX(-50%) rotate(0deg)";
        }
        onSpinEnd();
      }
    };

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinToken]);

  const gradientStops = wheel.options
    .map((_, i) => {
      const color = PALETTE[i % PALETTE.length];
      const start = i * segmentAngle;
      const end = (i + 1) * segmentAngle;
      const lineStart = Math.max(start, end - 0.6);
      return `${color} ${start}deg ${lineStart}deg, rgba(255,255,255,0.22) ${lineStart}deg ${end}deg`;
    })
    .join(", ");

  return (
    <div className="flex flex-col items-center">
      <div
        className={`relative ${discClassName} aspect-square select-none`}
        style={{ filter: "drop-shadow(0 0 60px rgba(124,58,237,0.35))" }}
      >
        {/* On-fire overlay for hot positive streaks — behind the rim. */}
        <WheelHeat heat={heat} />

        {/* Top pointer — pivots at its top so it flicks when struck */}
        <div
          ref={pointerRef}
          className="absolute -top-2 left-1/2 z-30 origin-top"
          style={{ transform: "translateX(-50%) rotate(0deg)" }}
        >
          <div className="h-0 w-0 border-x-[14px] border-t-[26px] border-x-transparent border-t-white drop-shadow-[0_0_8px_rgba(0,0,0,0.75)]" />
        </div>

        {/* Outer rim */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-b from-white/20 to-black/40 p-[3px]">
          <div className="relative h-full w-full overflow-hidden rounded-full border-4 border-[#1a1a22]">
            {/* Rotating colored disc */}
            <div
              ref={wheelRef}
              className="absolute inset-0 rounded-full"
              style={{
                background: `conic-gradient(${gradientStops})`,
                transform: `rotate(${restRotation}deg)`,
              }}
            >
              {wheel.options.map((opt, i) => {
                const centerDeg = i * segmentAngle + segmentAngle / 2;
                return (
                  <div
                    key={opt.name + i}
                    className="absolute inset-0"
                    style={{ transform: `rotate(${centerDeg}deg)` }}
                  >
                    {/* Sits near the rim on this segment's spoke; the inner
                        block counter-rotates to stay upright + horizontal. */}
                    <div className="absolute left-1/2 top-[10%] -translate-x-1/2">
                      <div
                        ref={(el) => {
                          labelRefs.current[i] = el;
                        }}
                        className="flex flex-col items-center leading-[1.05]"
                        style={{
                          transform: `rotate(${-(restRotation + centerDeg)}deg)`,
                        }}
                      >
                        {opt.name.split(" ").map((word, w) => (
                          <span
                            key={w}
                            className="text-[clamp(0.6rem,1.6vw,0.875rem)] font-extrabold uppercase tracking-tight text-white"
                            style={{ textShadow: LABEL_OUTLINE }}
                          >
                            {word}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Glossy highlight overlay */}
              <div
                className="pointer-events-none absolute inset-0 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle at 50% 28%, rgba(255,255,255,0.18), rgba(255,255,255,0) 45%)",
                }}
              />
            </div>

            {/* Result face — replaces the disc with its own segment color */}
            {showResult && resultOption && (
              <div
                className="animate-result-pop absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-full px-8 text-center"
                style={{ background: resultColor }}
              >
                <div
                  className="text-[clamp(0.6rem,1.6vw,0.875rem)] font-bold uppercase tracking-[0.35em] text-white/80"
                  style={{ textShadow: LABEL_OUTLINE }}
                >
                  {wheel.name}
                </div>
                <div
                  className="text-[clamp(1.5rem,5.5vw,3.75rem)] font-black uppercase leading-[1.02] text-white"
                  style={{ textShadow: LABEL_OUTLINE }}
                >
                  {resultOption.name.split(" ").map((word, w) => (
                    <div key={w}>{word}</div>
                  ))}
                </div>
                {showValue && (
                  <div
                    className="font-mono text-[clamp(1.25rem,4vw,3rem)] font-bold text-white"
                    style={{ textShadow: LABEL_OUTLINE }}
                  >
                    {resultOption.value >= 0 ? "+" : ""}
                    {resultOption.value.toLocaleString()}
                  </div>
                )}
                <div
                  className="pointer-events-none absolute inset-0 rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle at 50% 28%, rgba(255,255,255,0.18), rgba(255,255,255,0) 50%)",
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Center hub — Spin button, or a static decoration for the chaos
            wheel. Fades out when a result is shown. */}
        <div
          className={`absolute inset-0 z-20 flex items-center justify-center transition-opacity duration-300 ${
            showResult ? "pointer-events-none opacity-0" : "opacity-100"
          }`}
        >
          {spinButton ? (
            <button
              onClick={spinButton.onClick}
              disabled={spinButton.disabled}
              className="flex h-[26%] w-[26%] items-center justify-center rounded-full border-[5px] border-[#1a1a22] bg-gradient-to-b from-accent to-[#5b21b6] text-center text-[clamp(0.6rem,1.6vw,0.875rem)] font-extrabold uppercase leading-tight tracking-widest text-white shadow-[0_0_30px_rgba(124,58,237,0.6),inset_0_2px_6px_rgba(255,255,255,0.3)] transition hover:brightness-110 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {spinButton.label}
            </button>
          ) : (
            <div className="h-[26%] w-[26%] rounded-full border-[5px] border-[#1a1a22] bg-gradient-to-b from-accent to-[#5b21b6] shadow-[0_0_30px_rgba(124,58,237,0.6),inset_0_2px_6px_rgba(255,255,255,0.3)]" />
          )}
        </div>
      </div>

      {/* Footer slot — advance button / countdown, beneath the wheel */}
      <div className="mt-[clamp(1rem,3vh,1.5rem)] flex h-[clamp(3.5rem,8vh,4rem)] items-start justify-center">
        {footer}
      </div>
    </div>
  );
}
